import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../hooks/useLanguage';

export default function Navbar() {
  const { t, lang, setLang, isRTL } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to a section; sections live on the home page, so if we're not there
  // navigate to /home with the hash, otherwise scroll within the current page.
  const goToSection = (e, hash) => {
    e.preventDefault();
    setMobileOpen(false);
    if (location.pathname !== '/home') {
      navigate('/home' + hash);
    } else {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const languages = [
    { code: 'en', label: 'EN' },
    { code: 'fr', label: 'FR' },
    { code: 'ar', label: 'عر' },
  ];

  const navLinks = [
    { href: '#features', label: t('nav.features') },
    { href: '#delivery', label: t('nav.delivery') },
    { href: '#pricing', label: t('nav.pricing') },
    { href: '#how-it-works', label: t('nav.howItWorks') },
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      scrolled 
        ? 'bg-white/95 backdrop-blur-xl shadow-lg border-b border-gray-100' 
        : 'bg-white'
    }`}>
      <div className="w-full px-6 sm:px-8 lg:px-16 xl:px-24">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/home" className="flex items-center gap-3">
            <img src="/logo.jpg" alt="YouShop" className="h-14 w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => goToSection(e, link.href)}
                className="text-navy-600 hover:text-brand-500 font-medium transition-colors duration-300 relative group"
              >
                {link.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-brand-500 group-hover:w-full transition-all duration-300"></span>
              </a>
            ))}
          </div>

          {/* Right side */}
          <div className="hidden md:flex items-center gap-4">
            {/* Language Switcher */}
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              {languages.map((l) => (
                <button
                  key={l.code}
                  onClick={() => setLang(l.code)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-300 ${
                    lang === l.code
                      ? 'bg-navy-700 text-white shadow-sm'
                      : 'text-navy-500 hover:text-navy-700'
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>
            
            <Link to="/#get-started" className="btn-primary !py-2.5 !px-6 text-sm">
              {t('nav.getStarted')}
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden text-navy-700 p-2"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden animate-slide-down bg-white border-t border-gray-100 shadow-xl">
          <div className="px-6 py-6 space-y-4">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => goToSection(e, link.href)}
                className="block text-navy-600 hover:text-brand-500 font-medium py-2 transition-colors"
              >
                {link.label}
              </a>
            ))}
            <div className="flex items-center gap-2 pt-4 border-t border-gray-100">
              {languages.map((l) => (
                <button
                  key={l.code}
                  onClick={() => { setLang(l.code); setMobileOpen(false); }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    lang === l.code
                      ? 'bg-navy-700 text-white'
                      : 'bg-gray-100 text-navy-500'
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>
            <Link to="/#get-started" className="block btn-primary text-center !py-3 mt-4" onClick={() => setMobileOpen(false)}>
              {t('nav.getStarted')}
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}
